import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useToast } from '@/components/ui/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { PriceType, Currency } from '@/hooks/usePriceMetadata';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

// Base schema for type inference
const basePriceFormSchema = z.object({
  priceType: z.string().min(1),
  currencyCode: z.string().min(1),
  value: z.string().min(1),
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
});

export type PriceFormValues = z.infer<typeof basePriceFormSchema>;

interface PricingFormProps {
  productId: number;
  initialData?: {
    id?: number;
    priceType: string;
    currencyCode: string;
    value: number;
    validFrom?: string;
    validTo?: string | null;
  };
  onSuccess: () => void;
  onAdd: (values: PriceFormValues) => Promise<any>;
  onUpdate: (id: number, values: PriceFormValues) => Promise<any>;
}

export function PricingForm({
  productId,
  initialData,
  onSuccess,
  onAdd,
  onUpdate,
}: PricingFormProps) {
  const { toast } = useToast();
  const { priceTypes, currencies, loading: metaLoading, error: metaError } = usePriceMetadata();
  const queryClient = useQueryClient();
  const { defaultCurrency, defaultPriceType } = useOrgSettings();

  // Get translations
  const { getPricingFormLabels, getPricingFormNotification } = useProductDetailPageTranslations();
  const pricingFormLabels = getPricingFormLabels;

  const getDefaultValues = () => {
    // Use organization defaults if available, otherwise fallback to existing logic
    const defaultCurrencyCode = defaultCurrency?.iso_code || 'USD';
    const defaultPriceTypeCode = defaultPriceType?.code || 'base';

    if (initialData) {
      return {
        priceType: initialData.priceType,
        currencyCode: initialData.currencyCode,
        value: initialData.value.toString(),
        validFrom: initialData.validFrom || '',
        validTo: initialData.validTo || '',
      };
    }

    return {
      priceType: defaultPriceTypeCode,
      currencyCode: defaultCurrencyCode,
      value: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: '',
    };
  };

  const form = useForm<PriceFormValues>({
    resolver: zodResolver(basePriceFormSchema),
    defaultValues: getDefaultValues(),
  });

  // Reset form when initialData changes (e.g., when editing a new price)
  useEffect(() => {
    if (initialData) {
      const formValues = {
        priceType: initialData.priceType,
        currencyCode: initialData.currencyCode,
        value: initialData.value.toString(),
        validFrom: initialData.validFrom || new Date().toISOString().slice(0, 10),
        validTo: initialData.validTo || '',
      };
      form.reset(formValues);
    }
  }, [initialData]);

  // Reset form when organization defaults change (but not when editing existing data)
  useEffect(() => {
    if (!initialData && (defaultCurrency || defaultPriceType)) {
      const newDefaults = getDefaultValues();
      form.reset(newDefaults);
    }
  }, [defaultCurrency, defaultPriceType, initialData, form]);

  const isSubmitting = form.formState.isSubmitting;

  async function onSubmit(values: PriceFormValues) {
    try {
      if (initialData?.id) {
        // Update existing price
        await onUpdate(initialData.id, values);
      } else {
        // Add new price
        await onAdd(values);
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['prices'] });

      onSuccess();
      toast({
        title: pricingFormLabels.notifications.success,
        description: initialData?.id
          ? pricingFormLabels.notifications.priceUpdated
          : pricingFormLabels.notifications.priceAdded,
      });
    } catch (error) {
      logError(error as Error, {
        component: 'PricingForm',
        action: 'onSubmit',
        metadata: {
          productId,
          isUpdate: !!initialData?.id,
          priceId: initialData?.id,
          formValues: values,
          hasInitialData: !!initialData,
        },
      });
      toast({
        title: pricingFormLabels.notifications.error,
        description: pricingFormLabels.errors.saveFailed,
        variant: 'destructive',
      });
    }
  }

  // Show loading indicator if metadata is still loading
  if (metaLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="mb-2 size-8 animate-spin text-primary" />
        <p className="text-muted-foreground">{pricingFormLabels.loading.metadata}</p>
      </div>
    );
  }
  if (metaError) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="mb-2 text-destructive">{pricingFormLabels.errors.loadMetadata}</p>
        <p className="text-sm text-muted-foreground">{pricingFormLabels.errors.loadMetadataDesc}</p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="priceType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{pricingFormLabels.labels.priceType}</FormLabel>
              <FormControl>
                <Select disabled={isSubmitting} value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={pricingFormLabels.placeholders.selectPriceType} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(priceTypes) &&
                      priceTypes.map((pt: PriceType) => (
                        <SelectItem key={pt.id} value={pt.id.toString()}>
                          {pt.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="currencyCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{pricingFormLabels.labels.currency}</FormLabel>
              <FormControl>
                <Select disabled={isSubmitting} value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={pricingFormLabels.placeholders.selectCurrency} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(currencies) &&
                      currencies.map((c: Currency) => (
                        <SelectItem key={c.iso_code} value={c.iso_code}>
                          {c.iso_code} ({c.symbol})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{pricingFormLabels.labels.priceValue}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder={pricingFormLabels.placeholders.enterValue}
                  step="0.01"
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="validFrom"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{pricingFormLabels.labels.validFrom}</FormLabel>
              <FormControl>
                <Input type="date" disabled={isSubmitting} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="validTo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{pricingFormLabels.labels.validTo}</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  disabled={isSubmitting}
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder={pricingFormLabels.placeholders.neverExpires}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
          {initialData?.id
            ? pricingFormLabels.buttons.updatePrice
            : pricingFormLabels.buttons.addPrice}
        </Button>
      </form>
    </Form>
  );
}
